import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:transition-colors [&_svg]:duration-300",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md focus-visible:ring-primary/50 [&_svg]:text-primary-foreground hover:[&_svg]:text-primary-foreground",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow-md focus-visible:ring-destructive/50 [&_svg]:text-destructive-foreground hover:[&_svg]:text-destructive-foreground",
        outline:
          "border-[1.5px] border-primary/20 bg-background hover:bg-accent/5 hover:text-accent-foreground hover:border-primary/30 backdrop-blur-sm shadow-sm hover:shadow-sm focus-visible:ring-accent/50 [&_svg]:text-foreground/70 hover:[&_svg]:text-foreground/90 aria-expanded:bg-accent/10",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-sm hover:shadow-md focus-visible:ring-secondary/50 [&_svg]:text-secondary-foreground hover:[&_svg]:text-secondary-foreground",
        ghost: "text-foreground hover:bg-accent/10 hover:text-foreground data-[state=open]:bg-accent/20 focus-visible:ring-accent/50 [&_svg]:text-foreground/70 hover:[&_svg]:text-foreground/90",
        link: "text-primary hover:text-primary/90 underline-offset-4 hover:underline focus-visible:ring-primary/50 [&_svg]:text-primary hover:[&_svg]:text-primary/90",
        brand: "bg-primary/15 hover:bg-primary/20 text-primary hover:text-primary border-[1.5px] border-primary/30 hover:border-primary/40 font-semibold shadow-sm hover:shadow-sm [&_svg]:text-primary hover:[&_svg]:text-primary focus-visible:ring-primary/40 aria-expanded:bg-primary/25",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-xl px-3",
        lg: "h-11 rounded-xl px-8",
        icon: "h-10 w-10 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
